import { createDebugger } from '~/scripts/utils/debug.js';

const debug = createDebugger('product-recommendations', false);

class ProductRecommendations extends HTMLElement {
    connectedCallback() {
        this.init();

        if (this.dataset.reactToCart !== undefined) {
            this._onCartChange = () => this.reloadFromCart();
            document.addEventListener('theme:cart:change', this._onCartChange);
        }
    }

    disconnectedCallback() {
        if (this._onCartChange) {
            document.removeEventListener('theme:cart:change', this._onCartChange);
        }
    }

    async reloadFromCart() {
        try {
            const cart = await fetch('/cart.js').then((r) => r.json());
            const firstItem = cart.items?.[0];
            if (!firstItem) return;

            if (this.dataset.url) {
                // Update existing URL with new product_id
                const existingUrl = new URL(this.dataset.url, window.location.origin);
                existingUrl.searchParams.set('product_id', firstItem.product_id);
                this.dataset.url = existingUrl.toString();
            } else {
                // Empty cart on page load — build URL from scratch
                const limit = this.dataset.limit || 4;
                this.dataset.url = `${window.theme.routes.product_recommendations_url}?section_id=api-cart-recommendations&product_id=${firstItem.product_id}&limit=${limit}&intent=related`;
            }

            // Re-check deal collection membership for new first item
            const dealCollection = this.dataset.dealCollection;
            if (dealCollection) {
                const data = await fetch(`/collections/${dealCollection}/products.json?limit=250`).then((r) =>
                    r.json()
                );
                const inDeal = data.products.some((p) => String(p.id) === String(firstItem.product_id));
                this.dataset.productInDeal = String(inDeal);
            }

            // Update heading text
            const details = this.closest('details[data-collapsible]');
            const headingEl = details?.querySelector('[data-heading-text]');
            if (headingEl) {
                const usesDeal = this.dataset.productInDeal === 'true' && this.dataset.dealHeading;
                headingEl.textContent = usesDeal ? this.dataset.dealHeading : this.dataset.defaultHeading;
            }

            this.init();
        } catch (error) {
            debug.error('Failed to reload recommendations from cart', error);
        }
    }

    async init() {
        const url = this.dataset.url;
        if (!url) return;

        debug.log('Fetching recommendations', { url });

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const text = await response.text();
            const tmpl = document.createElement('template');
            tmpl.innerHTML = text;

            const el = tmpl.content.querySelector('product-recommendations');

            if (el && el.hasChildNodes()) {
                this.innerHTML = el.innerHTML;
                if (this.dataset.productInDeal === 'true' && this.dataset.dealCollection) {
                    await this.filterByDealCollection(this.dataset.dealCollection);
                }
                this.initCartButtons();

                // Unhide parent <details> if hidden (empty cart at page load)
                const details = this.closest('details[hidden]');
                if (details) details.removeAttribute('hidden');
            }
        } catch (error) {
            debug.error('Failed to load recommendations', error);
        }
    }
    async filterByDealCollection(handle) {
        try {
            const data = await fetch(`/collections/${handle}/products.json?limit=250`).then((r) => r.json());
            const dealIds = new Set(data.products.map((p) => String(p.id)));
            const allCards = [...this.querySelectorAll('[data-product-id]')];
            const toRemove = allCards.filter((card) => !dealIds.has(card.dataset.productId));
            // Only filter if at least one card would remain visible
            if (toRemove.length < allCards.length) {
                toRemove.forEach((card) => card.remove());
            }
        } catch (error) {
            debug.error('Failed to filter by deal collection', error);
        }
    }

    initCartButtons() {
        this.querySelectorAll('[data-add-to-cart]').forEach((button) => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                document.dispatchEvent(
                    new CustomEvent('theme:cart:add', {
                        detail: { button },
                        bubbles: false
                    })
                );
            });
        });
    }
}

if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
}
